import {
  Box,
  Button,
  ListItemIcon,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowRight, Cached, WarningRounded } from "@mui/icons-material";
import { NestedMenuItem } from "mui-nested-menu";
import { PlanTableFormat } from "../../../../types/planning/plan.type";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import { useDateSearchParamOrFallbackToToday } from "../../../../hooks/useDateSearchParamOrFallbackToToday";
import {
  BulkUpdateDeliveryRouteTrapStatusResponse,
  useDeliveryRoutesTrapsStatusChangeV2,
  useDeliveryRoutesTrapsStatusV3,
  DeliveryRoutesTrapsBulkStatus,
} from "../../../../hooks/react-query/useDeliveryRoutesTrapsStatus";

import { dateToInt } from "../../../../utils/dateTimeHelper";
import { displayMessage } from "../../../../constants/strings";
import { toast } from "../../../../utils/snackbarHelper";
import Loading from "../../../../components/shared/layout/Loading";
import { FlexColumn, FlexRow } from "../../../../components/shared/layout/Flex";
import { LightTooltip } from "../../../../components/shared/LightTooltip";
import { labelButtonStyle } from "../../../../components/shared/PopupMenu";
import ErrorList from "../../shared/ErrorList";

type ChangeStatusMenuItemProps = {
  routesOrTraps: PlanTableFormat[];
  isParentOpen: boolean;
  /** Omit if don't want to call it after the action */
  handleClose?: () => void;
  clearSelection?: () => void;
};

export const ChangeStatusMenuItem = ({
  routesOrTraps,
  isParentOpen,
  handleClose,
  clearSelection = () => {},
}: ChangeStatusMenuItemProps) => {
  const theme = useTheme();
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();

  const routeIds = routesOrTraps
    .filter((route) => route.type === "delivery_route")
    .map((route) => route.id);
  const trapIds = routesOrTraps
    .filter((trap) => trap.type === "delivery_trap")
    .map((trap) => trap.id);

  const onSuccess = (
    updateStatusResponse: BulkUpdateDeliveryRouteTrapStatusResponse
  ) => {
    const numberOfSuccessfullyUpdatedRoutes =
      updateStatusResponse.successfullyUpdatedIds.deliveryRoute.length;
    const numberOfSuccessfullyUpdatedTraps =
      updateStatusResponse.successfullyUpdatedIds.deliveryTrap.length;
    const numberOfFailedRoutes =
      updateStatusResponse.failedToUpdateIds.deliveryRoute.length;
    const numberOfFailedTraps =
      updateStatusResponse.failedToUpdateIds.deliveryTrap.length;

    if (
      numberOfSuccessfullyUpdatedRoutes > 0 &&
      numberOfSuccessfullyUpdatedTraps > 0
    ) {
      if (numberOfFailedRoutes === 0 && numberOfFailedTraps === 0) {
        toast(
          `${displayMessage.inbound.planning.bulkStatusChangedSuccessfully(numberOfSuccessfullyUpdatedRoutes, "delivery_route")}. ${displayMessage.inbound.planning.bulkStatusChangedSuccessfully(numberOfSuccessfullyUpdatedTraps, "delivery_trap")}.`,
          {
            variant: "success",
          }
        );
      } else {
        toast(
          `${displayMessage.inbound.planning.bulkStatusChangedPartialSuccess(numberOfSuccessfullyUpdatedRoutes, numberOfFailedRoutes, numberOfSuccessfullyUpdatedTraps, numberOfFailedTraps)}.`,
          { variant: "info", autoHideDuration: 7000 }
        );
      }
    } else if (numberOfSuccessfullyUpdatedRoutes > 0) {
      if (numberOfFailedRoutes === 0) {
        toast(
          `${displayMessage.inbound.planning.bulkStatusChangedSuccessfully(numberOfSuccessfullyUpdatedRoutes, "delivery_route")}.`,
          { variant: "success" }
        );
      } else if (trapIds.length === 0) {
        toast(
          displayMessage.inbound.planning.bulkStatusChangedPartialSuccessRoute(
            numberOfSuccessfullyUpdatedRoutes,
            numberOfFailedRoutes
          ),
          { variant: "info", autoHideDuration: 7000 }
        );
      } else {
        toast(
          `${displayMessage.inbound.planning.bulkStatusChangedPartialSuccess(numberOfSuccessfullyUpdatedRoutes, numberOfFailedRoutes, numberOfSuccessfullyUpdatedTraps, numberOfFailedTraps)}.`,
          { variant: "info", autoHideDuration: 7000 }
        );
      }
    } else if (numberOfSuccessfullyUpdatedTraps > 0) {
      if (numberOfFailedTraps === 0) {
        toast(
          `${displayMessage.inbound.planning.bulkStatusChangedSuccessfully(numberOfSuccessfullyUpdatedTraps, "delivery_trap")}.`,
          { variant: "success" }
        );
      } else if (routeIds.length === 0) {
        toast(
          displayMessage.inbound.planning.bulkStatusChangedPartialSuccessTrap(
            numberOfSuccessfullyUpdatedTraps,
            numberOfFailedTraps
          ),
          { variant: "info", autoHideDuration: 7000 }
        );
      } else {
        toast(
          `${displayMessage.inbound.planning.bulkStatusChangedPartialSuccess(numberOfSuccessfullyUpdatedRoutes, numberOfFailedRoutes, numberOfSuccessfullyUpdatedTraps, numberOfFailedTraps)}.`,
          { variant: "info", autoHideDuration: 7000 }
        );
      }
    } else {
      toast(`${displayMessage.inbound.planning.bulkStatusChangedFailed()}`);
    }

    clearSelection();
  };

  const { mutate: changeDeliveryRouteStatus, isPending } =
    useDeliveryRoutesTrapsStatusChangeV2({
      sicId: serviceCenter.id,
      date: dateToInt(selectedDate),
      trapIds,
      routeIds,
      onSuccess,
    });

  const changeStatus = (status: DeliveryRoutesTrapsBulkStatus) => {
    changeDeliveryRouteStatus(status);
    handleClose?.();
  };

  const { data: statuses, isLoading } = useDeliveryRoutesTrapsStatusV3(
    routeIds,
    trapIds
  );

  return (
    <NestedMenuItem
      delay={300}
      sx={{
        paddingX: "1rem",
      }}
      rightIcon={
        <ListItemIcon>
          <ArrowRight sx={{ color: theme.palette.grey[700] }} />
        </ListItemIcon>
      }
      leftIcon={
        <ListItemIcon>
          <Cached sx={{ padding: "6px 0", color: theme.palette.grey[700] }} />
        </ListItemIcon>
      }
      divider
      MenuProps={{
        anchorOrigin: { horizontal: "left", vertical: "top" },
        "aria-label": "statuses-menu",
      }}
      renderLabel={() => (
        <Button sx={labelButtonStyle} disableRipple>
          Change Status
        </Button>
      )}
      parentMenuOpen={isParentOpen}
    >
      {isPending || isLoading ? (
        <Stack
          alignItems="center"
          direction="row"
          gap={1}
          sx={{ px: 1, py: 2 }}
        >
          <Loading size={24} containerWidth={24} />
          <Typography>Loading statuses...</Typography>
        </Stack>
      ) : (
        statuses &&
        Object.entries(statuses).map(([statusName, details]) => (
          <MenuItem
            key={statusName}
            disableRipple={details.isNoneAvailableForTransition}
            sx={{
              cursor: "auto",
            }}
            onClick={() => {
              if (!details.isNoneAvailableForTransition) {
                changeStatus(details.status);
              }
            }}
          >
            <FlexColumn>
              <Typography
                sx={{
                  opacity: details.isNoneAvailableForTransition ? 0.38 : 1,
                }}
              >
                {statusName}
              </Typography>
              {(details.isNoneAvailableForTransition ||
                !details.isAllAvailableForTransition) && (
                <FlexColumn>
                  {Object.entries(details.errorDetails).map(
                    ([subject, messages]) => (
                      <FlexRow key={subject}>
                        <LightTooltip
                          aria-label={subject}
                          placement="left"
                          title={
                            <>
                              {Object.entries(messages).map(
                                ([message, data]) => (
                                  <Box
                                    key={message}
                                    sx={{
                                      overflowY: "auto",
                                      maxHeight: 300,
                                      paddingRight: 1,
                                    }}
                                  >
                                    {message}
                                    {data.map((entry) => (
                                      <Box
                                        key={entry.routeOrTrap}
                                        ml={2}
                                        mt={1}
                                        mb={1}
                                      >
                                        {entry.routeOrTrap}
                                        {entry.shipmentsData && (
                                          <ErrorList
                                            code={entry.code}
                                            errorDetail={entry.shipmentsData}
                                            serviceCenterCode={
                                              serviceCenter.code
                                            }
                                          />
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                )
                              )}
                            </>
                          }
                        >
                          <WarningRounded color="warning" />
                        </LightTooltip>
                        <Typography
                          ml={1}
                          fontSize={16}
                          sx={{
                            opacity: details.isNoneAvailableForTransition
                              ? 0.38
                              : 1,
                          }}
                        >
                          {subject}
                        </Typography>
                      </FlexRow>
                    )
                  )}
                </FlexColumn>
              )}
            </FlexColumn>
          </MenuItem>
        ))
      )}
    </NestedMenuItem>
  );
};
